import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from './ui/Button';
import AppIcon from './AppIcon';
import { cn } from '../utils/cn';

type PaginationProps = {
    currentPage: number;
    totalPages: number;
    onPageChange: (newPage: number) => void;
    showEdges?: boolean;
    variant?: 'full' | 'compact';
    hasPreviousPage?: boolean;
    hasNextPage?: boolean;
    pageSize?: number;
    pageSizeOptions?: number[];
    onPageSizeChange?: (size: number) => void;
    totalCount?: number;
};

const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    showEdges = true,
    variant = 'full',
    hasPreviousPage,
    hasNextPage,
    pageSize,
    pageSizeOptions = [10, 25, 50],
    onPageSizeChange,
    totalCount,
}) => {
    const { t } = useTranslation();
    const handlePageChange = (newPage: number) => {
        const page = clamp(newPage, 1, totalPages);
        if (page !== currentPage) onPageChange(page);
    };

    const isPrevDisabled = hasPreviousPage !== undefined ? !hasPreviousPage : currentPage === 1;
    const isNextDisabled = hasNextPage !== undefined ? !hasNextPage : currentPage === totalPages;
    const showNavigation = totalPages > 1;

    const getPageNumbers = (): (number | 'ellipsis')[] => {
        const pages: (number | 'ellipsis')[] = [];
        const show = 5;
        const half = Math.floor(show / 2);
        let start = Math.max(1, currentPage - half);
        const end = Math.min(totalPages, start + show - 1);
        start = Math.max(1, Math.min(start, end - show + 1));
        if (start > 1) {
            pages.push(1);
            if (start > 2) pages.push('ellipsis');
        }
        for (let p = start; p <= end; p++) pages.push(p);
        if (end < totalPages) {
            if (end < totalPages - 1) pages.push('ellipsis');
            pages.push(totalPages);
        }
        return pages;
    };

    // When there's no page-size selector, hide entirely for single-page results
    if (!showNavigation && !onPageSizeChange) return null;

    return (
        <div
            className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
            role="navigation"
            aria-label="pagination"
        >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                {onPageSizeChange && pageSize != null && (
                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{t('pagination.showPerPage')}</span>
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            className="h-9 rounded-md border border-border bg-card px-2 text-sm tabular-nums text-foreground transition-colors hover:border-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                        >
                            {pageSizeOptions.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </label>
                )}
                {totalCount != null && (
                    <span className="text-sm text-muted-foreground tabular-nums">
                        {t('pagination.totalItems', { count: totalCount })}
                    </span>
                )}
            </div>
            {showNavigation && (
                <div className="flex items-center justify-end gap-1">
                    {/* First/last are desktop affordances: on a phone they crowd out the arrows and
                        the position label, which are what people actually reach for. */}
                    {variant === 'full' && showEdges && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(1)}
                            disabled={isPrevDisabled}
                            aria-label={t('pagination.firstPage')}
                            className="hidden sm:inline-flex"
                        >
                            <AppIcon name="ChevronsLeft" size={16} className="rtl:rotate-180" />
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={isPrevDisabled}
                        aria-label={t('pagination.previousPage')}
                    >
                        <AppIcon name="ChevronLeft" size={16} className="rtl:rotate-180" />
                    </Button>

                    {/* The position label. `compact` has no numbered buttons at all, so without this it
                        was two arrows and no answer to "where am I?" — `pagination.pageOf` already
                        shipped in en/ar/fr and was never rendered. `full` falls back to it below the
                        `sm` breakpoint, where nine controls do not fit. */}
                    <span
                        className={cn(
                            'px-2 text-sm text-muted-foreground tabular-nums whitespace-nowrap',
                            variant === 'full' && 'sm:hidden',
                        )}
                        aria-live="polite"
                    >
                        {t('pagination.pageOf', { current: currentPage, total: totalPages })}
                    </span>

                    {variant === 'full' && (
                        <div className="hidden items-center gap-1 sm:flex">
                            {getPageNumbers().map((p, idx) =>
                                p === 'ellipsis' ? (
                                    <span
                                        key={`e-${idx}`}
                                        aria-hidden="true"
                                        className="inline-flex h-9 w-9 items-center justify-center text-sm text-muted-foreground"
                                    >
                                        &hellip;
                                    </span>
                                ) : (
                                    <Button
                                        key={p}
                                        variant={p === currentPage ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => handlePageChange(p)}
                                        // Fixed 36px cell: without it the row reflows every time the
                                        // digit count changes (9 -> 10) and the arrows jump under the cursor.
                                        className="min-w-9 justify-center px-0 tabular-nums"
                                        aria-current={p === currentPage ? 'page' : undefined}
                                        aria-label={t('pagination.pageOf', { current: p, total: totalPages })}
                                    >
                                        {p}
                                    </Button>
                                )
                            )}
                        </div>
                    )}

                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={isNextDisabled}
                        aria-label={t('pagination.nextPage')}
                    >
                        <AppIcon name="ChevronRight" size={16} className="rtl:rotate-180" />
                    </Button>
                    {variant === 'full' && showEdges && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={isNextDisabled}
                            aria-label={t('pagination.lastPage')}
                            className="hidden sm:inline-flex"
                        >
                            <AppIcon name="ChevronsRight" size={16} className="rtl:rotate-180" />
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default Pagination;
